import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';

export interface BuyTarget extends Doc {
  _id: string;
  cardId: number;
  card: string;
  priority: number;
  buycap: number;
}

type Model = BuyTarget;
type Refs = References<Model>;

export const BuyTargets = new Mongo.Collection<BuyTarget>('buyTargets');

export const listBuyTargets = DeclareMethod<ListMethodArg<BuyTarget>, ListMethodResponse<BuyTarget, Refs>>('listBuyTargets');
export const getBuyTarget = DeclareMethod<string, GetMethodResponse<BuyTarget, Refs>>('getBuyTarget');
export const createBuyTarget = DeclareMethod<Partial<BuyTarget>, string>('createBuyTarget');
export const updateBuyTarget = DeclareMethod<Partial<BuyTarget>, string>('updateBuyTarget');
export const deleteBuyTarget = DeclareMethod<string, undefined>('deleteBuyTarget');

/** Mirrors miscl/infinitebot/addtarget.py — bulk-create buy-targets selected
 *  by one of {id, name(+set,+foil), set, all} with optional price/rarity/foil
 *  filters. Existing buy-targets for the same cardId are replaced. */
export interface BulkAddBuyTargetsConfig {
  // exactly one of these selectors should be set
  id?: number;
  name?: string;
  set?: string;      // comma-separated for the "all cards in set(s)" selector
  all?: boolean;
  // filters (applied in name/set/all modes; NOT in id mode)
  rarity?: string;
  isfoil?: 'True' | 'False';
  minprice?: number;
  maxprice?: number;
  pricekey?: string;       // default 'manBuyPrice'
  withprices?: boolean;
  // foil filter for `name` selector only (Python: `db.filter(card, isfoil=...)`)
  foil?: boolean;
  // attributes for the new buy-target docs
  priority?: number;       // default 2
  buycap?: number;         // default 40
}
export interface BulkAddBuyTargetsResult {
  added: number;
  skipped: number;
  errors: string[];
}
export const bulkAddBuyTargets = DeclareMethod<BulkAddBuyTargetsConfig, BulkAddBuyTargetsResult>('bulkAddBuyTargets');

export const validateBuyTarget = async (target: Partial<BuyTarget>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, target, 'card', { required: true });
  return errors;
};

export const modelClass: ModelClass<Model, Refs> = {
  collection: BuyTargets,
  editableFields: ['cardId', 'card', 'priority', 'buycap'],
  listMethod: listBuyTargets,
  getMethod: getBuyTarget,
  createMethod: createBuyTarget,
  updateMethod: updateBuyTarget,
  deleteMethod: deleteBuyTarget,
  pathPrefix: '/buy-targets',
  modelName: 'BuyTarget',
  pluralName: 'Buy Targets',
  validate: validateBuyTarget,
};
